/**
 * SSH Access Summary Component
 * 
 * Displays SSH access status in a read-only format
 */

import React, { useState, useEffect } from 'react';
import edgeService from '../../services/edge.service';
import type { SSHStatusResponse } from '../../services/edge.service';
import { CheckCircle, XCircle, Loader, Terminal } from 'lucide-react';
import { TunnelMetrics } from './TunnelMetrics';

interface SSHAccessSummaryProps {
    edgeGuid: string;
}

export const SSHAccessSummary: React.FC<SSHAccessSummaryProps> = ({ edgeGuid }) => {
    const [status, setStatus] = useState<SSHStatusResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchStatus = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await edgeService.getSSHStatus(edgeGuid);
            setStatus(response);
        } catch (err: any) {
            setError(err.message || 'Failed to fetch SSH status');
            setStatus(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStatus();
    }, [edgeGuid]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-600">Loading SSH status...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center gap-3">
                    <XCircle className="h-5 w-5 text-red-600" />
                    <div>
                        <h3 className="font-semibold text-red-900">Error</h3>
                        <p className="text-sm text-red-700 mt-1">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* SSH Status Overview */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Terminal className="h-5 w-5" />
                    SSH Access Status
                </h3>

                <div className="grid grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                        {status?.tunnelKeyExists ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                            <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                            <div className="font-medium">Tunnel Key</div>
                            <div className="text-sm text-gray-600">
                                {status?.tunnelKeyExists ? 'Generated' : 'Not generated'}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {status?.hostCertInstalled ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                            <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                            <div className="font-medium">Host Certificate</div>
                            <div className="text-sm text-gray-600">
                                {status?.hostCertInstalled ? 'Installed' : 'Not installed'}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        {status?.tunnelActive ? (
                            <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                            <XCircle className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                            <div className="font-medium">Reverse Tunnel</div>
                            <div className="text-sm text-gray-600">
                                {status?.tunnelActive ? 'Active' : 'Not active'}
                            </div>
                        </div>
                    </div>
                </div>

                {status && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                            <div>Bastion: <code className="bg-gray-100 px-2 py-1 rounded">{status.bastionHost}:{status.bastionPort}</code></div>
                            <div className="mt-1">Tunnel Port: <code className="bg-gray-100 px-2 py-1 rounded">{status.tunnelPort}</code></div>
                        </div>
                    </div>
                )}
            </div>

            {/* Tunnel Metrics */}
            <TunnelMetrics edgeGuid={edgeGuid} />
        </div>
    );
};

export default SSHAccessSummary;
